import * as globals from './globals';
import { InputFilter, Lineup, ListFilter, Movie, MovieDate, RawMovie, UrlState } from './types';

export const sortAlphabetically = (a: string, b: string): -1 | 0 | 1 => {
  const A = a.toUpperCase();
  const B = b.toUpperCase();
  if (A < B) { return -1; }
  if (A > B) { return 1; }
  return 0;
};

export const sortAlphabeticallyByTitle = (a: { title: string }, b: { title: string }): -1 | 0 | 1 => sortAlphabetically(a.title, b.title);

export const timeStringToMinutes = (timeString: string): number => {
  const timeTokens = timeString.split(':');
  return Number(timeTokens[0]) * 60 + Number(timeTokens[1]);
};

export const dateTimeStringToMinutes = (dateTimeString: string): number => {
  const timeString = dateTimeString.split('T')[1].substring(0, 5);
  return timeStringToMinutes(timeString);
};

export const getUrlState = (): UrlState => {
  const urlStateString = window.location.hash.substring(1);
  if (urlStateString === '') {
    return {};
  }
  try {
    return JSON.parse(decodeURIComponent(urlStateString));
  } catch (e) {
    console.error(e);
  }
  return {};
};

export const setInputFilterValue = (inputFilter: InputFilter, value: string | null): void => {
  inputFilter.value = value;
  if (value == null) {
    inputFilter.compareValue = null;
    return;
  }
  switch (inputFilter.type) {
    case 'time':
      inputFilter.compareValue = timeStringToMinutes(value);
      break;
    case 'number':
      inputFilter.compareValue = parseFloat(value);
      break;
    default:
      inputFilter.compareValue = new RegExp(value.trim().replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i');
  }
};

export const setListFilterValue = (listFilter: ListFilter, value: string[]): void => {
  listFilter.currentValues = value;
};

export const fetchMoviesFromFile = async (year: number): Promise<RawMovie[] | null> => {
  try {
    return await Promise.resolve(await (await fetch(`${process.env.PUBLIC_URL}/data/${globals.JSON_DATA_FILE_PREFIX}${year}.json`)).json());
  } catch (e) {
    console.error(e);
    return Promise.resolve(null);
  }
};

export const preProcessMovies = (rawMovies: RawMovie[], year: number): Movie[] => {
  const movies: Movie[] = [];
  const stringToMoviesMap: Map<string, Movie[]> = new Map();

  for (const rawMovie of rawMovies) {
    if (rawMovie.title == null) {
      console.error('Movie with no title.');
      continue;
    }
    if (rawMovie.website_url == null) {
      console.error('Movie with no website URL.');
      continue;
    }
    const imageUrl = rawMovie.image_url?.split('/').at(-1) ?? null;
    const movie: Movie = {
      title: rawMovie.title,
      websiteUrl: rawMovie.website_url,
      descriptionShort: rawMovie.description_short ?? null,
      description: rawMovie.description ?? null,
      details: {
        countries: rawMovie.countries ?? [],
        year: rawMovie.year ?? null,
        durationInMinutes: rawMovie.duration_in_minutes ?? null,
        language: rawMovie.language ?? null,
        camera: rawMovie.camera ?? null,
      },
      category: rawMovie.category ?? null,
      section: rawMovie.section ?? null,
      dates: (rawMovie.dates as MovieDate[]) ?? [],
      fullText: [
        rawMovie.title ?? '',
        rawMovie.category ?? '',
        (rawMovie.year != null) ? `${rawMovie.year}` : '',
        rawMovie.language ?? '',
        rawMovie.camera ?? '',
        (rawMovie.dates != null) ? rawMovie.dates.map((date) => date.venue).join(' ') : '',
        rawMovie.people ?? '',
        rawMovie.description_short ?? '',
        rawMovie.description ?? '',
        rawMovie.website_category ?? '',
        rawMovie.section ?? '',
      ].join(' ').replace(/\s+/g, ' '),
      people: rawMovie.people ? rawMovie.people.split('\n\n').map((person: string) => person.trim()).filter((person: string) => person !== '') : [],
      imageUrl: (imageUrl != null) ? `${process.env.PUBLIC_URL}/images/${year}/${encodeURIComponent(imageUrl)}` : null,
      detailsStringified: null,
      lineups: [],
    };

    const details: string[] = [];
    if (movie.details.countries.length > 0) details.push(movie.details.countries.join('/'));
    if (movie.details.year != null) details.push(movie.details.year.toString());
    if (movie.details.durationInMinutes != null) details.push(`${movie.details.durationInMinutes} Min.`);
    if (movie.details.language != null) details.push(movie.details.language);
    if (movie.details.camera != null) details.push(movie.details.camera);
    movie.detailsStringified = (details.length > 0) ? details.join(', ') : null;

    for (const date of movie.dates) {
      date.minutesOfDay = dateTimeStringToMinutes(date.datetime);
      date.dayString = new Date(date.datetime).toLocaleDateString(globals.LOCALE, globals.LOCALE_DATE);

      const key = date.datetime + date.venue;
      const moviesAtSameTimeAndPlace = stringToMoviesMap.get(key);
      if (moviesAtSameTimeAndPlace != null) {
        moviesAtSameTimeAndPlace.push(movie);
      } else {
        stringToMoviesMap.set(key, [movie]);
      }
    }

    movies.push(movie);
  }

  for (const movie of movies) {
    const lineupMap: Map<string, Lineup> = new Map();
    for (const date of movie.dates) {
      date.stringified = `${new Date(date.datetime).toLocaleDateString(globals.LOCALE, globals.LOCALE_DATE_TIME)}, ${date.venue}`;
      const key = date.datetime + date.venue;
      if (!stringToMoviesMap.has(key)) {
        continue;
      }
      const moviesAtSameTimeAndPlace = stringToMoviesMap.get(key);
      if (moviesAtSameTimeAndPlace != null && moviesAtSameTimeAndPlace.length > 1) {
        const moviesAtSameTimeAndPlaceSorted = moviesAtSameTimeAndPlace.sort(sortAlphabeticallyByTitle);
        const lineupKey = moviesAtSameTimeAndPlaceSorted.map((m) => m.title).join('');
        lineupMap.set(lineupKey, { key: lineupKey, date, movies: moviesAtSameTimeAndPlaceSorted });
      }
    }
    movie.lineups = Array.from(lineupMap.values());
  }

  return movies;
};
